import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect, useState } from 'react';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import Screens from '../../constants/routing/Screens';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector, hasProductsSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { useHistory } from 'react-router-dom';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import { getMerchCardsToShowForBundle } from '../../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../../constants/routing/NavItems';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { useAppSelector } from '../../reducers';
import { isUngatedForMarketingPlusSelector } from '../../selectors/GatingSelectors';
import { bundleProductsForBundleTypeSelector } from '../../selectors/ProductsSelectors';
import { getPageViewEvent } from '../../utils/event-tracking/interactions';
import { trackEvent } from '../../utils/event-tracking/track-event';
import { getIsAcceptanceTest } from '../../utils/testUtils';
import BannerSection from '../banners/BannerSection';
import CustomerSupport from '../common/CustomerSupport';
import IntegrationsSection from '../common/Integrations';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import ErrorPage from '../error-page/ErrorPage';
import BundleTile from '../merch-card/BundleTile';
import { borderStyle, StyledCardGrid } from '../merch-card/MerchCardSharedComponents';
import ProductPageSubtitle from '../product-pages/ProductPageSubtitle';
import ProductPageTitle, { ProductPageHeader } from '../product-pages/ProductPageTitle';
import MobileWrapperNoTabs from './containers/MobileWrapperNoTabs';
import MobileBundleFeatureTable from './MobileBundleFeatureTable';
import MobileProductLevelSelectorButtons from './MobileProductLevelSelectorButtons';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const commonStyles = {
  gridColumnStart: 1,
  paddingLeft: 28,
  paddingRight: 28,
  borderRight: borderStyle,
  borderLeft: borderStyle
};
const MobileMarketingPlusPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('marketing-plus');
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const productLevels = getMerchCardsToShowForBundle({
    bundleType: MARKETING_PLUS_SUITE
  });
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const screen = Screens.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const hasProductsSucceeded = useAppSelector(hasProductsSucceededSelector);
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  const isAcceptanceTest = getIsAcceptanceTest();
  const history = useHistory();
  useEffect(() => {
    if (!hasProductsSucceeded) return;
    if (!isUngatedForMarketingPlus && !isAcceptanceTest) {
      history.replace('/suite');
    }
    trackEvent(getPageViewEvent({
      screen
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProductsSucceeded]);
  const highlightedProducts = useAppSelector(state => {
    if (hasPricingAndPurchasingFailed) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    }).map(({
      apiName
    }) => apiName);
  });
  useBusinessObjectives({
    highlightedProducts
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts({
      productLine: ProductLines.MARKETING,
      productLevels
    }),
    productLine: ProductLines.MARKETING
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts({
      productLine: ProductLines.CMS,
      productLevels
    }),
    productLine: ProductLines.CMS
  });
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  if (!hasProductsSucceeded) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(MobileWrapperNoTabs, {
    navItem: navItem,
    navMarkerName: "MARKETING_PLUS_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: screen
    }), /*#__PURE__*/_jsx(FailedLoadBanner, {
      hasFailed: hasPricingAndPurchasingFailed
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
      productLevels: productLevels,
      onClick: setSelectedProductLevel,
      selectedProductLevel: selectedProductLevel
    }), /*#__PURE__*/_jsx(StyledCardGrid, {
      columns: 1,
      children: /*#__PURE__*/_jsx(BundleTile, {
        bundleType: MARKETING_PLUS_SUITE,
        productLevel: selectedProductLevel,
        screen: screen,
        commonStyles: commonStyles
      })
    }), /*#__PURE__*/_jsx(MobileBundleFeatureTable, {
      apiNames: highlightedProducts,
      productLevel: selectedProductLevel,
      screen: screen
    }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
      phoneSourceId: "bundle"
      // phoneSourceId="marketingplus-footer"
      ,
      screen: screen
    })]
  });
};
export default MobileMarketingPlusPage;